import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useGlobalContext } from '@/context/GlobalContext';
import { filterTopItems } from '@/utils/formatItems';
import { useOnClickOutside } from 'usehooks-ts';

import { useCourseData, useTopCollegeData, useTopCourseData, useTopExamData } from '@/lib/api/apiData';
import { useEntityData } from '@/hooks/useEntityData';
import useFetchEntities from '@/hooks/useEntityDataWithoutCaching';

const useUniversalSearchbarHook = () => {
  const { searchBoxOpen, setSearchBoxOpen } = useGlobalContext();

  const { courseData } = useCourseData();
  const { topCollegeData } = useTopCollegeData();
  const { topExamData } = useTopExamData();
  const { topCourseData } = useTopCourseData();
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const collegeQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['college_name', 'college_url', 'abbreviation', 'is_top', 'is_online_college'],
      filters: {
        $or: [
          { college_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        country: {
          fields: ['id', 'country_name', 'country_url'],
        },
        college_navbars: { fields: ['id', 'title'] },
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const { data: searchCollegeData, isLoading: isSearchCollegeLoading } = useFetchEntities(
    'colleges',
    collegeQueryParams,
    inputValue,
  );

  const examQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['exam_name', 'abbreviation', 'exam_url', 'is_top'],
      filters: {
        $or: [
          { exam_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        exam_navbars: { fields: ['id', 'title'], limit: 1 },
      },
      publicationState: 'live',
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const newsQueryParams = useMemo(
    () => ({
      sort: 'updatedAt:desc',
      // fields: ["author_name", "excerpt", "name", "url", "is_top", "updatedAt"],
      fields: ['name', 'url', 'is_top', 'updatedAt'],
      populate: {
        // { name: "featured_image", fields: ["id", "url", "name"] },
        // { name: "streams", fields: ["id", "stream_name"] },
        categories: { fields: ['category_name'] },
      },
    }),
    [inputValue],
  );
  const courseQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      fields: ['course_name', 'abbreviation', 'course_url', 'is_top'],
      filters: {
        $or: [
          { course_name: { $startsWithi: inputValue.toLowerCase() } },
          { abbreviation: { $startsWithi: inputValue.toLowerCase() } },
        ],
      },
      populate: {
        logo: { fields: ['id', 'url', 'name'] },
        course_navbars: { fields: ['id', 'title'], limit: 1 },
        page_data: {
          fields: ['id'],
        },
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );

  const { data: searchExamData, isLoading: isSearchExamLoading }: any = useFetchEntities(
    'exams',
    examQueryParams,
    inputValue,
  );
  const instituteQueryParams = useMemo(
    () => ({
      sort: 'display_sequence:asc',
      publicationState: 'live',
      fields: ['id', 'institute_name', 'institute_address', 'institute_url', 'institute_google_reviews'],
      populate: {
        logo: { fields: ['url'] },
      },
      filters: {
        $or: [
          { institute_name: { $containsi: inputValue.toLowerCase() } },
          { abbreviation: { $containsi: inputValue.toLowerCase() } },
        ],
      },
      pagination: {
        page: 1,
        pageSize: 25,
      },
    }),
    [inputValue],
  );
  const { data: searchInstituteData, isLoading: isSearchInstituteLoading } = useFetchEntities(
    'institute-details', 
    instituteQueryParams,
    inputValue,
  );
  const { data: newsData }: any = useEntityData('news-and-blogs', newsQueryParams);
  const { data: searchCourseData, isLoading: isSearchCourseData } = useFetchEntities(
    'courses',
    courseQueryParams,
    inputValue,
  );

  const filteredCollegeData = filterTopItems(topCollegeData);
  const filteredExamData = filterTopItems(topExamData);
  const filteredNewsData = filterTopItems(newsData?.data?.results);
  const filteredCourseData = topCourseData;

  const filterCourse = searchCourseData?.data?.results?.filter((course: any) => course?.page_data?.length > 0);

  useOnClickOutside(containerRef, (event) => {
    if (inputRef?.current?.contains(event.target as Node) || dropdownRef?.current?.contains(event.target as Node)) {
      return;
    }
    setSearchBoxOpen(false);
    setInputValue('');
    document.body.style.overflow = '';
  });

  const handleInputFocus = () => {
    setSearchBoxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleLinkClick = () => {
    setSearchBoxOpen(false);
    setInputValue('');
    document.body.style.overflow = '';
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const filterResults = () => {
    const combinedResults = [
      ...(searchCollegeData?.data?.results ?? []),
      ...(searchExamData?.data?.results ?? []),
      ...(searchCourseData?.data?.results ?? []),
      ...(searchInstituteData?.data?.results ?? []), // Add this line
    ].slice(0, 15);

    return combinedResults;
  };

  const matchedResults = inputValue?.length > 2 ? filterResults() : [];

  useEffect(() => {
    if (searchBoxOpen) {
      document.body.style.overflow = 'hidden';
      inputRef.current?.focus();
    }
  }, [searchBoxOpen]);

  // const handleResultClick = (result: any) => {
  //   if (result.institute_url) {
  //     router.push(`/institutes/${result.institute_url}`);
  //   } else if (result.college_url) {
  //     router.push(`/colleges/${result.college_url}`);
  //   } else if (result.exam_url) {
  //     router.push(`/exams/${result.exam_url}`);
  //   } else if (result.course_url) {
  //     router.push(`/courses/${result.course_url}`);
  //   }
  //   setInputValue('');
  //   setSearchBoxOpen(false);
  // };

  return {
    containerRef,
    dropdownRef,
    searchBoxOpen,
    inputRef,
    inputValue,
    handleInputChange,
    handleLinkClick,
    handleInputFocus,
    matchedResults,
    filteredCollegeData,
    filteredExamData,
    filteredNewsData,
    filteredCourseData,
    isSearchCollegeLoading,
    isSearchExamLoading,
    isSearchCourseData, // courseData,
    topCollegeData,
    // topCourseData,
    courseData,
    isSearchInstituteLoading,
    //topSuggestions,
    //handleTopSuggestionClick,
    // handleResultClick,
  };
};

export default useUniversalSearchbarHook;
